/**
 * Loading an **enhanced** model — the optional, Blender-built `.glb` beside a
 * baked one (`docs/engineering/enhanced-models.md`, `tools/enhanced`).
 *
 * The `.glb` is already in this app's world frame. It is built in the baked
 * model's own axes (Z-up, nose +Y) and exported through glTF's default Y-up
 * conversion, which is `(x, y, z) -> (x, z, -y)` — the same transform
 * `MODEL_TO_WORLD_PITCH_RAD` applies to a baked LOD. Nothing is rotated here,
 * deliberately: an enhanced model that needed a second correction would be a
 * bug in the generator, not something to paper over at load.
 *
 * **It carries the ramp, not just a colour.** Each vertex has the palette entry
 * the original's own shade walk lands on at the bake's reference attitude
 * (`COLOR_0`) *and* the two numbers that walk it — `shadeAnchor` and
 * `shadeHalfWidth` — plus the `.CLG` `paintGroup` that claimed the face. So an
 * enhanced aeroplane re-shades as it banks through exactly the material a baked
 * one uses (`ramp-material.ts`), and the player's paint shop can move its fabric
 * the same way `model-repaint.ts` moves a baked LOD's. Blender writes those
 * three as glTF custom attributes and `GLTFLoader` lower-cases the names, so the
 * first thing done to a loaded scene is renaming the trio to what the shader
 * reads. A `.glb` built before they existed simply has none, and then the
 * material falls through to the baked vertex colour for every face — which is
 * the pre-existing behaviour, not a failure.
 *
 * `/assets`'s turntable wants none of that and keeps the plain unlit material
 * (`applyFlatMaterials`): a model on a stand never banks.
 *
 * The named sub-assemblies a loaded rung carries — its propeller, its pilot,
 * its guns — and the showing, hiding and turning of them are
 * `enhanced-nodes.ts`'s. This file loads the file and owns its materials.
 */

import * as THREE from "three/webgpu";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { SHADE_ANCHOR_ATTRIBUTE, SHADE_HALF_WIDTH_ATTRIBUTE } from "./ramp-material.ts";
import { PALETTE_ENTRY_COUNT, type BakedPaletteBody } from "./ramp-shade.ts";
import { paintShopHalfWidth, referenceShadedIndex } from "./model-repaint.ts";
import { readStreamedBody, type BytesProgress } from "./streamed-body.ts";

/** The `.CLG` group a face belongs to, or `PAINT_GROUP_NONE`. Written by `tools/blender/rb_enhanced/export.py` as `_PAINTGROUP`. */
export const PAINT_GROUP_ATTRIBUTE = "paintGroup";
/** What the generator writes for a face the `.CLG` never claimed — a marking, a strut, a wheel. */
export const PAINT_GROUP_NONE = -1;

/** glTF custom-attribute name (as `GLTFLoader` lower-cases it) → the name the app's material and repaint read. */
const RAMP_ATTRIBUTE_NAMES: readonly (readonly [string, string])[] = [
  ["_shadeanchor", SHADE_ANCHOR_ATTRIBUTE],
  ["_shadehalfwidth", SHADE_HALF_WIDTH_ATTRIBUTE],
  ["_paintgroup", PAINT_GROUP_ATTRIBUTE],
];

/** Frees every geometry and material under a group returned by `loadEnhancedModel`. */
export function disposeEnhancedModel(group: THREE.Object3D): void {
  group.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry.dispose();
    const material = child.material as THREE.Material | THREE.Material[];
    for (const entry of Array.isArray(material) ? material : [material]) entry.dispose();
  });
}

/**
 * Renames the generator's glTF custom attributes to the names
 * `ramp-material.ts` and `repaintEnhanced` read. Silent when they are absent:
 * that is a `.glb` from before the generator wrote them, and it still draws.
 */
export function adoptRampAttributes(root: THREE.Object3D): THREE.Object3D {
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    for (const [loaded, wanted] of RAMP_ATTRIBUTE_NAMES) {
      const attribute = child.geometry.getAttribute(loaded);
      if (!attribute) continue;
      child.geometry.setAttribute(wanted, attribute);
      child.geometry.deleteAttribute(loaded);
    }
  });
  return root;
}

/** Swaps every material in a loaded scene for one the caller owns — the flat unlit one, or a ramp material. */
export function applyMaterial(root: THREE.Object3D, material: THREE.Material): THREE.Object3D {
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const previous = child.material as THREE.Material | THREE.Material[];
    for (const entry of Array.isArray(previous) ? previous : [previous]) entry.dispose();
    child.material = material;
  });
  return root;
}

/** The flat, unlit, vertex-coloured material `/assets` draws both models with. */
export function applyFlatMaterials(root: THREE.Object3D): THREE.Object3D {
  return applyMaterial(root, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide }));
}

/**
 * The player's paint shop, applied to an enhanced model in place.
 *
 * `colours` is the six `.CLG` group colours the aeroplane is to wear —
 * `personalSchemeColours(factory, paint)`, the same array `repaintLod` takes.
 * The work is per *vertex* rather than per face because that is how the
 * generator stores it (it shares no vertex between polygons), and it is the same
 * three-field move a baked repaint makes: a new anchor, that colour's own ramp
 * width, and the reference-attitude colour recomputed so an unshaded face and
 * the palette-less fallback material still agree with the bake.
 *
 * Returns false when the model carries no `paintGroup` attribute — a `.glb`
 * built before the generator wrote one, which the caller must then draw in its
 * factory colours or fall back to the baked model rather than show wrong paint.
 */
export function repaintEnhanced(root: THREE.Object3D, colours: readonly number[], palette: BakedPaletteBody): boolean {
  let repainted = false;
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const groups = child.geometry.getAttribute(PAINT_GROUP_ATTRIBUTE);
    const anchors = child.geometry.getAttribute(SHADE_ANCHOR_ATTRIBUTE);
    const halfWidths = child.geometry.getAttribute(SHADE_HALF_WIDTH_ATTRIBUTE);
    const normals = child.geometry.getAttribute("normal");
    const vertexColours = child.geometry.getAttribute("color");
    if (!groups || !anchors || !halfWidths) return;
    repainted = true;
    for (let vertex = 0; vertex < groups.count; vertex++) {
      const group = groups.getX(vertex);
      if (group < 0) continue;
      const anchor = colours[group];
      if (anchor === undefined || anchor === anchors.getX(vertex)) continue;
      const halfWidth = paintShopHalfWidth(anchor) ?? halfWidths.getX(vertex);
      anchors.setX(vertex, anchor);
      halfWidths.setX(vertex, halfWidth);
      if (!vertexColours) continue;
      // The generator builds in the model frame and the exporter turns
      // `(x, y, z)` into `(x, z, -y)`, so the model-space Y the reference sun is
      // dotted with is the *negated world Z* of the normal that arrived here.
      const modelUpwards = normals ? -normals.getZ(vertex) : 0;
      const index = referenceShadedIndex(anchor, halfWidth, halfWidth > 0, modelUpwards);
      const clamped = Math.max(0, Math.min(PALETTE_ENTRY_COUNT - 1, index));
      const [red, green, blue] = palette.colors[clamped] ?? [0, 0, 0];
      vertexColours.setXYZ(vertex, (red as number) / 255, (green as number) / 255, (blue as number) / 255);
    }
    anchors.needsUpdate = true;
    halfWidths.needsUpdate = true;
    if (vertexColours) vertexColours.needsUpdate = true;
  });
  return repainted;
}

/**
 * The node name a LOD rung is exported under
 * (`tools/blender/rb_enhanced/detail.py`'s `NODE_PREFIX`), and the pattern that
 * reads the rung number back off it. A `.glb` whose nodes do not match this is
 * a single-rung model — either scenery, or an airframe built before the ladder
 * existed — and `enhancedRungs` says so by returning one rung.
 */
const LOD_NODE_PATTERN = /^LOD(\d+)$/;

/**
 * The ladder inside one loaded `.glb`, coarsest last.
 *
 * The four rungs ride in **one file** deliberately. A rung per file would mean a
 * fetch at the moment an aeroplane crosses a switching distance, and a pop that
 * lasts as long as the network does; four resident rungs switch within the
 * frame. The cost is about 60% more bytes per airframe, paid once per type per
 * sortie (`docs/engineering/enhanced-models.md` §9).
 *
 * Returns `[root]` unchanged when the file carries no `LOD<n>` nodes, so every
 * caller that just wants "the model" — the `/assets` turntable, the career
 * aeroplane, `loadEnhancedModel` below — is unaffected by the ladder existing.
 */
export function enhancedRungs(root: THREE.Object3D): readonly THREE.Object3D[] {
  const rungs: { index: number; object: THREE.Object3D }[] = [];
  for (const child of root.children) {
    const matched = LOD_NODE_PATTERN.exec(child.name);
    if (matched) rungs.push({ index: Number(matched[1]), object: child });
  }
  if (rungs.length === 0) return [root];
  return rungs.sort((a, b) => a.index - b.index).map((rung) => rung.object);
}

/** The four bytes every binary glTF file starts with, `glTF`, as the little-endian `uint32` the container specifies. */
const GLB_MAGIC = 0x46546c67;

/**
 * Refuses a body that is not a binary glTF, and says *why* rather than letting
 * `GLTFLoader` throw its own message about a container it was never handed.
 *
 * The case worth naming: a static host with no `/enhanced/*` route answers this
 * fetch with the SPA's own `index.html` and a **200**
 * (`enhanced-diagnostics.ts`). `response.ok` is true, the buffer is HTML, and
 * the caller's fall back to the baked aeroplane is correct but silent. Reading
 * the four magic bytes turns that into one sentence naming the route.
 */
function assertGlb(buffer: ArrayBuffer, url: string): void {
  if (buffer.byteLength >= 4 && new DataView(buffer).getUint32(0, true) === GLB_MAGIC) return;
  const head = new TextDecoder().decode(new Uint8Array(buffer, 0, Math.min(40, buffer.byteLength)));
  throw new Error(
    `${url} answered 200 with something that is not a binary glTF (starts ${JSON.stringify(head)}) — ` +
      "a host with no /enhanced/* route is serving its SPA fallback for it.",
  );
}

/**
 * The `.glb` for `path`, with every LOD rung it carries, coarsest last.
 *
 * It arrives carrying whatever PBR material the exporter declared, and **that
 * must not survive the load**: there is no lighting rig anywhere in this app for
 * one to participate in, so a `MeshStandardMaterial` renders the aeroplane
 * black. (It did, in `/assets`, for exactly as long as this function forgot to
 * replace it.) The flat material here is the default; `/fly` swaps in its own
 * ramp material on top, which is a material's worth of garbage per load and
 * cheaper than a caller that has to remember.
 *
 * The rungs are **detached from the glTF scene** rather than left under it: a
 * caller either draws one of them or hangs them off a `THREE.LOD`, and neither
 * wants a parent that would draw all four at once.
 */
export async function loadEnhancedRungs(
  path: string,
  onBytes?: BytesProgress,
  /**
   * The manifest's own decoded length for this `.glb`. Passed through to
   * `readStreamedBody` as the progress denominator, because `Content-Length` is
   * the *compressed* length on every host that serves this tree the way
   * production does (G58).
   */
  decodedBytes?: number,
): Promise<readonly THREE.Object3D[]> {
  const url = `/enhanced/${path}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`failed to load enhanced model ${path}: ${response.status} ${response.statusText}`);
  }
  const buffer = await readStreamedBody(response, onBytes, decodedBytes);
  assertGlb(buffer, url);
  const gltf = await new GLTFLoader().parseAsync(buffer, "");
  const scene = adoptRampAttributes(gltf.scene);
  const rungs = enhancedRungs(scene);
  for (const rung of rungs) {
    if (rung !== scene) rung.removeFromParent();
    // A flat material **per rung**, not one for the file: a caller that keeps
    // one rung and frees the others would otherwise free the material the rung
    // it kept is drawn with.
    applyFlatMaterials(rung);
  }
  return rungs;
}

/**
 * The finest rung of `path`, ready to draw — the model this function has always
 * returned.
 *
 * Everything that shows one aeroplane at a fixed size wants exactly this and
 * nothing else: the `/assets` comparison, the career turntable, the plane
 * viewer, and the player's own machine in `/fly`, which is never far enough
 * away for a coarser rung to be right. The rungs it does not return are freed
 * here rather than left for the caller to notice.
 */
export async function loadEnhancedModel(path: string): Promise<THREE.Object3D> {
  const [finest, ...coarser] = await loadEnhancedRungs(path);
  for (const rung of coarser) disposeEnhancedModel(rung);
  if (!finest) throw new Error(`enhanced model ${path} has no geometry`);
  return finest;
}
